import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { DashboardService } from "services";
import {
  AdminDashboardScope,
  DashboardMonthlyProgressResponse,
  DashboardYearlyProgressResponse,
} from "types";

import MonthlyProgress from "./MonthlyProgress";
import classes from "./ProgressSection.module.css";
import YearlyProgress from "./YearlyProgress";

interface ProgressSectionProps {
  className?: string;
  scope?: AdminDashboardScope;
}

interface ProgressMetrics {
  realizedAmount: number;
  targetAmount: number;
  projectedAmount?: number;
  currency: string;
}

const DEFAULT_MONTHLY_PROGRESS: ProgressMetrics = {
  realizedAmount: 0,
  targetAmount: 0,
  projectedAmount: 0,
  currency: "CHF",
};

const DEFAULT_YEARLY_PROGRESS: ProgressMetrics = {
  realizedAmount: 0,
  targetAmount: 0,
  currency: "CHF",
};

const mapMonthlyProgressResponse = (data: DashboardMonthlyProgressResponse): ProgressMetrics => ({
  realizedAmount: data?.realized ?? 0,
  targetAmount: data?.target ?? 0,
  projectedAmount: data?.projection ?? 0,
  currency: "CHF",
});

const mapYearlyProgressResponse = (data: DashboardYearlyProgressResponse): ProgressMetrics => ({
  realizedAmount: data?.realized ?? 0,
  targetAmount: data?.target ?? 0,
  currency: "CHF",
});

const getProgress = (value: number, target: number) => {
  if (target <= 0) {
    return 0;
  }

  return Math.min(value / target, 1);
};

const formatCurrencyAmount = (value: number, locale: string, currency: string) =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)} ${currency}`;

const formatAmount = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

const formatCompactMillions = (value: number) => {
  const amountInMillions = value / 1_000_000;
  const decimals = Number.isInteger(amountInMillions) ? 0 : 1;

  return `${amountInMillions.toFixed(decimals)}M`;
};

const ProgressSection = ({ className, scope = "admin" }: ProgressSectionProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "sr";
  const [monthlyProgress, setMonthlyProgress] = useState<ProgressMetrics>(DEFAULT_MONTHLY_PROGRESS);
  const [yearlyProgress, setYearlyProgress] = useState<ProgressMetrics>(DEFAULT_YEARLY_PROGRESS);

  useEffect(() => {
    let mounted = true;

    const loadProgress = async () => {
      const [monthlyResult, yearlyResult] = await Promise.allSettled([
        DashboardService.getMonthlyProgress(scope),
        DashboardService.getYearlyProgress(scope),
      ]);

      if (!mounted) {
        return;
      }

      setMonthlyProgress(
        monthlyResult.status === "fulfilled"
          ? mapMonthlyProgressResponse(monthlyResult.value)
          : DEFAULT_MONTHLY_PROGRESS,
      );

      setYearlyProgress(
        yearlyResult.status === "fulfilled"
          ? mapYearlyProgressResponse(yearlyResult.value)
          : DEFAULT_YEARLY_PROGRESS,
      );
    };

    loadProgress();

    return () => {
      mounted = false;
    };
  }, [scope]);

  return (
    <section className={[classes.root, className].filter(Boolean).join(" ")}>
      <div className={classes.content}>
        <MonthlyProgress
          title={t("dashboard.admin.progress.monthlyTitle")}
          value={monthlyProgress.realizedAmount}
          target={monthlyProgress.targetAmount}
          valueLabel={formatAmount(
            monthlyProgress.realizedAmount,
            locale,
          )}
          targetLabel={formatCurrencyAmount(
            monthlyProgress.targetAmount,
            locale,
            monthlyProgress.currency,
          )}
          projectionLabel={`${t("dashboard.admin.progress.projection")}: ${formatCurrencyAmount(
            monthlyProgress.projectedAmount ?? 0,
            locale,
            monthlyProgress.currency,
          )}`}
        />

        <YearlyProgress
          title={t("dashboard.admin.progress.yearlyTitle")}
          progress={getProgress(
            yearlyProgress.realizedAmount,
            yearlyProgress.targetAmount,
          )}
          targetLabel={formatCompactMillions(yearlyProgress.targetAmount)}
          realizedLabel={formatCompactMillions(yearlyProgress.realizedAmount)}
        />
      </div>
    </section>
  );
};

export default ProgressSection;
